import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { refusalText } from "../../lib/refusal.js";
import type { ClipCue } from "../../services/generated/client.js";
import {
  connectToGame,
  type GameLink,
  type GameView,
  type LinkHealth,
} from "../../services/gameLink.js";
import { HttpError } from "../../services/http.js";
import { realtimeUrl } from "../../services/realtime.js";

/** Where the connection is. */
export type LinkStatus =
  /** Still opening. */
  | "connecting"
  /** Live. */
  | "connected"
  /** No seat for this browser — the caller should offer to join. */
  | "no-seat"
  /** Something else went wrong; `error` says what, in words. */
  | "failed";

/** What the page gets. */
export interface Game {
  status: LinkStatus;
  /**
   * Whether the live connection is currently usable.
   *
   * Separate from `status` on purpose: a socket that dropped mid-round should
   * say so *over the board that is already on screen*, not replace it with a
   * spinner. The board a player is looking at is still the last true thing they
   * were told.
   */
  health: LinkHealth;
  /** Human-readable, only when `status` is `failed`. */
  error: string | null;
  /** The merged state, once anything has arrived. */
  view: GameView | null;
  /** The last clip cue, for the audio element. */
  cue: ClipCue | null;
  /** The connection, for sending intent. */
  link: GameLink | null;
  /** Try again — after joining, or after a failure. */
  reconnect: () => void;
}

/**
 * Hold one connection to a party's game for the life of the page.
 *
 * # The link is state, and its identity is stable
 *
 * It is tempting to keep a socket in a ref — it is not "for rendering" — but a
 * ref read during render is a value React has not agreed to re-render for, so a
 * page would show "connecting" until something else happened to update it.
 * Held as state instead, and set exactly once per connection attempt, so an
 * effect keyed on it (`LobbyPanel` loads the genre tree that way) runs once and not per
 * published snapshot.
 *
 * # A missing seat is a status, not an error
 *
 * `socketTicket` answers 401 when the browser holds no device cookie, which is
 * the *normal* state for somebody opening a link for the first time. Rendering a
 * failure there would put an error on the screen of every new player.
 */
export function useGame(): Game {
  const { t } = useTranslation();
  const [status, setStatus] = useState<LinkStatus>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<GameView | null>(null);
  const [cue, setCue] = useState<ClipCue | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [link, setLink] = useState<GameLink | null>(null);
  const [health, setHealth] = useState<LinkHealth>("live");

  const reconnect = useCallback(() => {
    // Everything else resets inside the effect's own async body, where setting
    // state is a response to something rather than a cascade during commit.
    setLink(null);
    setStatus("connecting");
    setError(null);
    setHealth("live");
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let opened: GameLink | null = null;

    void (async () => {
      try {
        const connection = await connectToGame(realtimeUrl(), {
          onState: (next) => {
            if (!cancelled) {
              setView(next);
            }
          },
          onClip: (next) => {
            if (!cancelled) {
              setCue(next);
            }
          },
          // A dropped socket is recovered inside the link (a fresh ticket, and
          // `watch` again). All this does is let the screen say so — and, when
          // recovery genuinely fails, rebuild the whole connection, which is the
          // one thing the link cannot do to itself.
          onHealth: (next) => {
            if (cancelled) {
              return;
            }
            setHealth(next);
            // `lost` is a transport failure and the right answer is to try
            // again, silently — the player is mid-round and a rebuild is
            // invisible when it works.
            //
            // `expired` is not a failure at all: the server decided this device
            // stopped counting, and retrying would be refused for exactly the
            // same reason it was refused the first time. That one needs a person,
            // so it goes on screen instead of into a reconnect loop.
            if (next === "lost") {
              setStatus("connecting");
              setAttempt((n) => n + 1);
            }
          },
        });

        if (cancelled) {
          // React's development double-effect, or a fast unmount. Closing here
          // rather than leaving it is what stops a page accumulating sockets
          // every time it remounts.
          connection.disconnect();
          return;
        }
        opened = connection;
        setLink(connection);
        setStatus("connected");
        setHealth("live");
      } catch (cause) {
        if (cancelled) {
          return;
        }
        if (cause instanceof HttpError && cause.status === 401) {
          setStatus("no-seat");
          return;
        }
        setStatus("failed");
        setError(refusalText(cause, t));
      }
    })();

    return () => {
      cancelled = true;
      opened?.disconnect();
    };
    // `t` is a dependency because the effect words its own failure. It changes
    // only when somebody switches language, and reconnecting on that is
    // harmless — the seat is a cookie and the socket reopens.
  }, [attempt, t]);

  return { status, health, error, view, cue, link, reconnect };
}
